import {
  appendEquityExtraBenchmarkColumns,
  appendTxnBenchmarkColumns,
} from "../../../utils/reports/transactionBenchmarkColumns";
import { appendAdjustedCostPriceColumn } from "../../../utils/reports/reportHoldingColumns";
import { REPORT_GAIN_TYPE_COLUMN_LABEL } from "../../../utils/reports/reportDisplayLabels";
import {
  appendHoldingLevelCostColumns,
  orderTransactionCostBenchmarkAndMemberColumns,
  withTxnMetadataAndBenchmarkColumns,
} from "../../../utils/reports/transactionMetadataColumns";
import {
  FieldColumn,
  getColumnsForPrivateEquityReportType,
  PRIVATE_EQUITY_ALL_COMMON_COLUMNS,
  PRIVATE_EQUITY_TABLE_SEGMENT,
  PRIVATE_EQUITY_TRANSACTION_COLUMNS,
  PRIVATE_EQUITY_UNREALIZED_HOLDING_COLUMNS,
  INVESTOR_COLUMN,
} from "./privateEquityReportConfig";

export const REPORT_BUILDER_VISIBLE_COLUMN_COUNT = 7;

const UNREALIZED_CAGR_COLUMN: FieldColumn = { key: "cagr", value: "CAGR", minWidth: 96 };

const PNL_COLUMN_KEYS = new Set([
  "gainLoss",
  "gainLossPercentage",
  "gainLossPerc",
  "unrealisedPNL",
  "unrealisedPNLPercentage",
  "realisedPnl",
  "realisedPNLPercentage",
]);

const UNREALIZED_HOLDING_TYPE_COLUMN: FieldColumn = {
  key: "type",
  value: REPORT_GAIN_TYPE_COLUMN_LABEL,
  locked: true,
  minWidth: 132,
};

const REPORT_DAYS_COLUMN: FieldColumn = {
  key: "days",
  value: "Avg. Days",
  minWidth: 88,
};

const REPORT_COST_COLUMN_KEYS = [
  "stt",
  "stampDuty",
  "otherExpenses",
  "brokerage",
] as const;

const PRIVATE_EQUITY_ALL_REPORT_AFTER_ADJ_COLUMNS: FieldColumn[] = [
  { key: "cagr", value: "CAGR", minWidth: 96 },
];

const ALL_REPORT_HIDDEN_KEYS = new Set(["isin", "folioNumber", "folioNo"]);

const withUnrealizedCagrColumn = (cols: FieldColumn[]): FieldColumn[] => {
  if (cols.some((c) => c.key === "cagr")) return cols;
  const without = cols.filter((c) => c.key !== "cagr");
  const pnlIdx = without.findIndex((c) => PNL_COLUMN_KEYS.has(c.key));
  const insertAt = pnlIdx >= 0 ? pnlIdx : without.length;
  return [
    ...without.slice(0, insertAt),
    UNREALIZED_CAGR_COLUMN,
    ...without.slice(insertAt),
  ];
};

const stripHiddenAllColumns = (cols: FieldColumn[]): FieldColumn[] =>
  cols.filter((c) => !ALL_REPORT_HIDDEN_KEYS.has(c.key));

const withUnrealizedHoldingTypeColumn = (cols: FieldColumn[]): FieldColumn[] => {
  if (!cols.length) return cols;

  let result = stripHiddenAllColumns(
    cols.map((c) =>
      c.key === "type" ? { ...c, value: REPORT_GAIN_TYPE_COLUMN_LABEL } : c,
    ),
  );

  if (result.some((c) => c.key === "type")) {
    return withUnrealizedCagrColumn(result);
  }

  const nameIdx = result.findIndex(
    (c) => c.key === "symbol" || c.key === "schemeName",
  );
  const insertAt = nameIdx >= 0 ? nameIdx + 1 : 1;

  return withUnrealizedCagrColumn([
    ...result.slice(0, insertAt),
    UNREALIZED_HOLDING_TYPE_COLUMN,
    ...result.slice(insertAt),
  ]);
};

const withTxnSchemeNameColumn = (cols: FieldColumn[]): FieldColumn[] => {
  if (cols.some((c) => c.key === "schemeName" || c.key === "symbol")) return cols;
  return [
    { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 220 },
    ...cols,
  ];
};

const ensureReportDaysColumn = (columns: FieldColumn[]): FieldColumn[] => {
  if (columns.some((c) => c.key === "days")) return columns;
  return [...columns, REPORT_DAYS_COLUMN];
};

const stripDaysColumn = (columns: FieldColumn[]): FieldColumn[] =>
  columns.filter((c) => c.key !== "days");

const orderDaysColumnBeforeStt = (columns: FieldColumn[]): FieldColumn[] => {
  const withoutDays = columns.filter((c) => c.key !== "days");
  const daysCol = columns.find((c) => c.key === "days") ?? REPORT_DAYS_COLUMN;
  const firstCostIdx = withoutDays.findIndex((c) =>
    REPORT_COST_COLUMN_KEYS.includes(
      c.key as (typeof REPORT_COST_COLUMN_KEYS)[number],
    ),
  );
  const insertAt = firstCostIdx >= 0 ? firstCostIdx : withoutDays.length;
  return [
    ...withoutDays.slice(0, insertAt),
    daysCol,
    ...withoutDays.slice(insertAt),
  ];
};

const stripMemberNameColumn = (cols: FieldColumn[]): FieldColumn[] =>
  orderTransactionCostBenchmarkAndMemberColumns(cols, {
    includeMemberName: false,
  }) as FieldColumn[];

const orderTrailingColumnsAfterAdjCost = (
  columns: FieldColumn[],
  trailingColumns: FieldColumn[],
): FieldColumn[] => {
  const trailingKeySet = new Set(trailingColumns.map((c) => c.key));
  const without = columns.filter((c) => !trailingKeySet.has(c.key));
  const trailingCols = trailingColumns.map((def) => {
    const existing = columns.find((c) => c.key === def.key);
    return existing ?? def;
  });
  const adjIdx = without.findIndex((c) => c.key === "adjustedCostPrice");
  const insertAt = adjIdx >= 0 ? adjIdx + 1 : without.length;
  return [
    ...without.slice(0, insertAt),
    ...trailingCols,
    ...without.slice(insertAt),
  ];
};

const appendPrivateEquityBenchmarkAndCostColumns = (
  columns: FieldColumn[],
  options?: { placeDaysBeforeStt?: boolean },
): FieldColumn[] => {
  let cols = columns;
  if (options?.placeDaysBeforeStt) {
    cols = stripDaysColumn(cols);
  }
  cols = stripMemberNameColumn(cols);
  if (options?.placeDaysBeforeStt) {
    cols = orderDaysColumnBeforeStt(ensureReportDaysColumn(cols));
  }
  return cols;
};

const buildPrivateEquityHoldingPreset = (): FieldColumn[] => {
  let cols = appendAdjustedCostPriceColumn(
    withUnrealizedCagrColumn(PRIVATE_EQUITY_UNREALIZED_HOLDING_COLUMNS),
  );
  cols = appendHoldingLevelCostColumns(cols, PRIVATE_EQUITY_TABLE_SEGMENT);
  cols = appendTxnBenchmarkColumns(cols);
  return appendEquityExtraBenchmarkColumns(cols);
};

const buildPrivateEquityHoldingColumns = (): FieldColumn[] =>
  appendPrivateEquityBenchmarkAndCostColumns(
    withUnrealizedHoldingTypeColumn(buildPrivateEquityHoldingPreset()),
    { placeDaysBeforeStt: true },
  );

const buildPrivateEquityRealizedColumns = (): FieldColumn[] => {
  let cols = withTxnMetadataAndBenchmarkColumns(
    getColumnsForPrivateEquityReportType("Realized"),
    PRIVATE_EQUITY_TABLE_SEGMENT,
    { includeBenchmarks: true },
  ) as FieldColumn[];
  cols = withUnrealizedCagrColumn(stripHiddenAllColumns(cols));
  cols = appendEquityExtraBenchmarkColumns(cols);
  return stripMemberNameColumn(cols);
};

const buildPrivateEquityAllColumns = (): FieldColumn[] => {
  let cols = appendTxnBenchmarkColumns(
    appendHoldingLevelCostColumns(
      appendAdjustedCostPriceColumn(PRIVATE_EQUITY_ALL_COMMON_COLUMNS),
      PRIVATE_EQUITY_TABLE_SEGMENT,
    ),
  );
  cols = ensureReportDaysColumn(cols);
  const keys = new Set(cols.map((c) => c.key));
  PRIVATE_EQUITY_ALL_REPORT_AFTER_ADJ_COLUMNS.forEach((col) => {
    if (!keys.has(col.key)) cols.push(col);
  });
  cols = appendEquityExtraBenchmarkColumns(cols);
  cols = orderTrailingColumnsAfterAdjCost(
    cols,
    PRIVATE_EQUITY_ALL_REPORT_AFTER_ADJ_COLUMNS,
  );
  return appendPrivateEquityBenchmarkAndCostColumns(stripHiddenAllColumns(cols), {
    placeDaysBeforeStt: true,
  });
};

const buildPrivateEquityTransactionColumns = (): FieldColumn[] => {
  let cols = withTxnSchemeNameColumn(
    appendEquityExtraBenchmarkColumns(
      withTxnMetadataAndBenchmarkColumns(
        PRIVATE_EQUITY_TRANSACTION_COLUMNS,
        PRIVATE_EQUITY_TABLE_SEGMENT,
        { includeBenchmarks: true },
      ) as FieldColumn[],
    ),
  );
  cols = withUnrealizedCagrColumn(cols);
  return stripMemberNameColumn(cols);
};

export const getBaseColumnsForReportType = (reportType: string): FieldColumn[] => {
  switch (reportType) {
    case "Holding":
      return buildPrivateEquityHoldingColumns();
    case "Realized":
      return buildPrivateEquityRealizedColumns();
    case "All":
      return buildPrivateEquityAllColumns();
    case "Transaction":
      return buildPrivateEquityTransactionColumns();
    default:
      return getColumnsForPrivateEquityReportType(reportType);
  }
};

export const buildSelectableColumns = (reportType: string): FieldColumn[] => [
  INVESTOR_COLUMN,
  ...getBaseColumnsForReportType(reportType),
];

export const buildEffectiveColumns = (
  reportType: string,
  visibleColumnKeys: string[],
): FieldColumn[] => {
  let columns = buildSelectableColumns(reportType);
  if (visibleColumnKeys.length > 0) {
    const visible = new Set(visibleColumnKeys);
    columns = columns.filter((c) => visible.has(c.key));
  }
  return columns;
};

export const getDefaultVisibleColumnKeys = (reportType: string): string[] =>
  buildSelectableColumns(reportType).map((c) => c.key);
